import type { ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

interface EmptyStateProps {
  /** Material Symbol icon name */
  icon: IconName;
  /** Text shown below the icon — displayed uppercase */
  label: string;
  /**
   * Optional sentence under the label: what to do next, or why the list is
   * empty. Small, centred, sentence case — part of the empty state, never a
   * loose `<p>` under it (that one sat far below, outside the block's gap).
   */
  hint?: ReactNode;
  className?: string;
}

/**
 * Standard empty-state placeholder used when a list, table, or panel has no items.
 *
 * Standard component for ALL empty states across admin and tenant.
 * Import from `@shared` — never implement inline.
 *
 * Text is centred and the block keeps a side gutter, so a label that wraps
 * (German, a narrow card on a phone) stays centred under the icon instead of
 * running edge to edge from the left.
 *
 * @example
 * <EmptyState icon="inbox" label="No hay pedidos" />
 * <EmptyState icon="lan" label="Ningún agente" hint="Crea uno e instálalo en tu red." />
 */
export default function EmptyState({ icon, label, hint, className = '' }: EmptyStateProps) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-6 gap-3 text-center ${className}`}>
      <Icon name={icon} className="!text-[48px] text-slate-200 dark:text-slate-700" />
      <p className="max-w-md text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      {hint != null && hint !== false && hint !== '' ? (
        <p className="max-w-md text-xs text-text-sub dark:text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}
